// pkg195 — payment method 'unknown' displays as Unknown on the statement preview — delivered 2026-10-31
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import { firmDateYmd, formatFirmDate } from '../utils/firmDate';


const th = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600 };
const td = { padding: '8px 12px', fontFamily: 'Arial, sans-serif', fontSize: 13 };

const MOBILE_STYLES = `
.gen-invoice-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 320px);
  gap: 16px;
  align-items: start;
}
.gen-invoice-summary {
  position: sticky;
  top: 16px;
}
@media (max-width: 800px) {
  .gen-invoice-grid {
    grid-template-columns: 1fr;
  }
  .gen-invoice-summary {
    position: static;
  }
  .gen-invoice-page .form-row {
    flex-direction: column;
  }
  .gen-invoice-page .form-row .form-group {
    flex: 1 1 auto !important;
    width: 100%;
    min-width: 0;
  }
}
`;

function fmtMoney(n) {
  return '$' + (parseFloat(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Wave 3b — small label maps used by the statement preview tables.
const METHOD_LABELS = {
  check: 'Check', cash: 'Cash', ach: 'ACH / eCheck', wire: 'Wire',
  credit_card: 'Credit Card', money_order: 'Money Order',
  trust_transfer: 'Trust Transfer', other: 'Other', unknown: 'Unknown',
};
function methodLabel(m) { return METHOD_LABELS[m] || m || ''; }
const TRUST_TYPE_LABELS = {
  deposit: 'Deposit', withdrawal: 'Withdrawal',
  transfer_to_operating: 'Transfer to Operating', adjustment: 'Adjustment',
};
function trustTypeLabel(t) { return TRUST_TYPE_LABELS[t] || t || ''; }

function fmtDate(s) {
  if (!s) return '';
  const formatted = formatFirmDate(s);
  return formatted === '—' ? '' : formatted;
}

function todayISO() {return new Date().toISOString().slice(0, 10);}
function plusDaysISO(days) {
  const d = new Date();d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function AdminGenerateInvoicePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('clientId');


  const [step, setStep] = useState(preselectedClientId ? 2 : 1);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(preselectedClientId || '');
  const [preview, setPreview] = useState(null);
  const [selectedEntries, setSelEntries] = useState(new Set());
  const [selectedExpenses, setSelExpenses] = useState(new Set());
  const [issueDate, setIssueDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState(plusDaysISO(30));
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [discount, setDiscount] = useState('');
  const [tax, setTax] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Wave 3b — Account-statement settings ──────────────────────────────────
  //
  // The four statement fields the invoice carries:
  //   - statementFrom / statementTo : date window to show activity in.
  //     `statementFrom = ''` (empty) means OPEN-ENDED → "all time", which is
  //     the default. Attorney can pick a specific From date to narrow it.
  //   - includeVoided                : whether voided rows show on the statement
  //   - trustTypes                   : which of the four trust txn types to include
  // The "show statement" toggle (`showStatement`) maps directly to the new
  // `include_statement` boolean column on the invoice. When off, the invoice
  // falls back to the legacy (no-statement) rendering.
  const [showStatement, setShowStatement] = useState(true);
  const [statementFrom, setStatementFrom] = useState('');   // '' = all time
  const [statementTo, setStatementTo]     = useState('');
  const [includeVoided, setIncludeVoided] = useState(false);
  const [trustTypes, setTrustTypes]       = useState(new Set(['deposit', 'withdrawal', 'transfer_to_operating', 'adjustment']));
  // Wave 3b revision: preview tables open by default — the attorney almost
  // always wants to see what's about to land on the invoice.
  const [statementPreviewOpen, setStatementPreviewOpen] = useState(true);

  // Load client list at start
  useEffect(() => {
    adminApi.listClients().then(setClients).catch((err) => setError(err.message));
  }, []);

  // When client is chosen, fetch unbilled items
  useEffect(() => {
    if (!clientId) return;
    setLoading(true);setError('');
    adminApi.previewInvoiceItems(clientId).
    then((data) => {
      setPreview(data);
      // Default: select all unbilled items
      setSelEntries(new Set(data.entries.map((e) => e.id)));
      setSelExpenses(new Set(data.expenses.map((x) => x.id)));
      // Wave 3b: seed the statement window from the backend's suggestion.
      // suggested_statement_from is now null by default (= "all time"), so
      // we leave the From input empty. To is seeded to today. Attorney can
      // narrow either side by picking a date.
      setStatementFrom(data.suggested_statement_from || '');
      if (data.suggested_statement_to) setStatementTo(data.suggested_statement_to);
    }).
    catch((err) => setError(err.message)).
    finally(() => setLoading(false));
  }, [clientId]);

  // Live total calculation
  const totals = (() => {
    if (!preview) return { fees: 0, exp: 0, total: 0, noChargeHours: 0, noChargeCount: 0 };
    let fees = 0;
    let noChargeHours = 0;
    let noChargeCount = 0;
    for (const e of preview.entries) {
      if (!selectedEntries.has(e.id)) continue;
      if (e.billable && e.rate_used != null) {
        fees += parseFloat(e.units) * parseFloat(e.rate_used);
      } else if (!e.billable) {
        // Wave 2: selected non-billable entries appear on the invoice as
        // "no charge" line items — track their hours separately so the
        // summary shows what the client is being shown at no cost.
        noChargeHours += parseFloat(e.units) || 0;
        noChargeCount += 1;
      }
    }
    let exp = 0;
    for (const x of preview.expenses) {
      if (selectedExpenses.has(x.id) && x.billable) {
        exp += parseFloat(x.amount);
      }
    }
    const disc = parseFloat(discount) || 0;
    const tx = parseFloat(tax) || 0;
    return {
      fees: +fees.toFixed(2),
      exp: +exp.toFixed(2),
      discount: disc, tax: tx,
      total: +(fees + exp + tx - disc).toFixed(2),
      noChargeHours: +noChargeHours.toFixed(2),
      noChargeCount,
    };
  })();

  function toggleEntry(id) {
    setSelEntries((prev) => {const n = new Set(prev);n.has(id) ? n.delete(id) : n.add(id);return n;});
  }
  function toggleExpense(id) {
    setSelExpenses((prev) => {const n = new Set(prev);n.has(id) ? n.delete(id) : n.add(id);return n;});
  }
  function selectAllEntries() {setSelEntries(new Set((preview?.entries || []).map((e) => e.id)));}
  function selectNoEntries() {setSelEntries(new Set());}
  function selectAllExpenses() {setSelExpenses(new Set((preview?.expenses || []).map((x) => x.id)));}
  function selectNoExpenses() {setSelExpenses(new Set());}

  // ── Wave 3b — live-filtered statement preview ─────────────────────────────
  // The preview endpoint returns the client's full payment / refund /
  // reversal / trust history. We filter client-side as the attorney narrows
  // the date range, toggles "include voided", or changes the trust-type
  // checkboxes — instant updates with no network round-trip. An empty
  // `statementFrom` means "all time, no lower bound."
  function toggleTrustType(t) {
    setTrustTypes((prev) => {
      const n = new Set(prev);
      if (n.has(t)) n.delete(t); else n.add(t);
      return n;
    });
  }
  const filteredStatement = (() => {
    if (!preview || !showStatement) {
      return { payments: [], refunds: [], reversals: [], trust: [] };
    }
    const from = statementFrom || '';
    const to   = statementTo   || '';
    const within = (d) => {
      if (!d) return false;
      const iso = firmDateYmd(d);
      if (from && iso < from) return false;   // empty `from` = no lower bound
      if (to   && iso > to)   return false;
      return true;
    };
    const voidedOk = (row) => includeVoided || row.status !== 'voided';
    return {
      payments:  (preview.statement_payments  || []).filter((p) => within(p.payment_date)  && voidedOk(p)),
      refunds:   (preview.statement_refunds   || []).filter((r) => within(r.refund_date)   && voidedOk(r)),
      reversals: (preview.statement_reversals || []).filter((r) => within(r.reversal_date) && voidedOk(r)),
      trust:     (preview.statement_trust     || []).filter((t) => within(t.txn_date)      && trustTypes.has(t.type)),
    };
  })();

  async function handleCreate() {
    if (!selectedEntries.size && !selectedExpenses.size) {
      return setError('Select at least one time entry or expense.');
    }
    if (!subject.trim()) { // pkg177
      return setError('Enter a subject for the invoice — it is the heading on the document and the one line your client sees in their portal.');
    }
    setLoading(true);setError('');
    try {
      const payload = {
        clientId,
        entryIds: [...selectedEntries],
        expenseIds: [...selectedExpenses],
        issueDate, dueDate,
        subject: subject.trim(),
        notes: notes || null,
        internalNotes: internalNotes || null,
        discount: parseFloat(discount) || 0,
        tax: parseFloat(tax) || 0,
      };
      // Wave 3b: send `includeStatement` to drive whether the invoice has
      // a statement section at all. When on, the four statement settings
      // also flow through. `statementFrom` may be empty/null → "all time"
      // (no lower bound on the activity window).
      if (showStatement) {
        payload.includeStatement   = true;
        payload.statementFrom      = statementFrom || null;
        payload.statementTo        = statementTo   || null;
        payload.includeVoided      = includeVoided;
        // trustTypesIncluded: send the explicit list (may be empty) so the
        // backend stores exactly what the attorney picked. Backend semantics:
        //   NULL  → show all four (default for invoices that never set it)
        //   []    → show none (attorney unchecked everything)
        //   [..]  → only those
        payload.trustTypesIncluded = [...trustTypes];
      } else {
        payload.includeStatement = false;
      }
      const inv = await adminApi.createInvoice(payload);
      navigate(`/admin/dashboard/invoices/${inv.id}`);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  // ── STEP 1: pick a client ────────────────────────────────────────────────
  if (step === 1 || !clientId) {
    return (
      <div className="gen-invoice-page">
        <style>{MOBILE_STYLES}</style>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
          <button className="btn btn-sm" onClick={() => navigate('/admin/dashboard/invoices')}>← Invoices</button>
        </div>
        <h1 className="page-title">Generate Invoice</h1>
        <p className="page-subtitle">Step 1 of 2 — Choose the client to invoice.</p>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="card">
          <div className="form-group">
            <label>Client</label>
            <select value={clientId} onChange={(e) => {setClientId(e.target.value);setStep(2);}}>
              <option value="">— Select a client —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      </div>);

  }

  // ── STEP 2: pick line items + invoice details ────────────────────────────
  // Mask the client name shown in the subtitle. The preview.client object
  // carries name + id from the backend; we mask the displayed name and use
  // the id for the deterministic tag.
  return (
    <div className="gen-invoice-page">
      <style>{MOBILE_STYLES}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {/* Back-to-client is the natural navigation here: users almost
            always reach this page from a specific client's detail page.
            We show it first (leftmost) and keep "Invoices" as a secondary
            option for users who came from the invoices list. */}
        {clientId &&
        <button className="btn btn-sm" onClick={() => navigate(`/admin/clients/${clientId}`)}>
          ← Back to Client{preview?.client ? `: ${preview.client.name}` : ''}
        </button>
        }
        <button className="btn btn-sm" onClick={() => navigate('/admin/dashboard/invoices')}>← Invoices</button>
        {!preselectedClientId &&
        <button className="btn btn-sm" onClick={() => {setClientId('');setStep(1);setPreview(null);}}>Change Client</button>
        }
      </div>
      <h1 className="page-title">Generate Invoice</h1>
      <p className="page-subtitle">
        {preview?.client ? `Invoicing ${preview.client.name}.` : 'Loading...'} Select the items to include and set invoice details.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {loading && !preview && <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', fontSize: 13 }}>Loading items not yet invoiced...</div>}

      {preview &&
      <>
          {/* Time entries — Wave 2 includes non-billable entries with
              "No charge" styling. Both billable and non-billable default to
              selected; attorney can uncheck either group. */}
          {(() => {
            const billableCount    = preview.entries.filter((e) => e.billable).length;
            const nonBillableCount = preview.entries.length - billableCount;
            return (
              <div className="section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <span>
                  Time Entries Ready to Invoice ({preview.entries.length})
                  {nonBillableCount > 0 &&
                  <span style={{ fontSize: 12, fontWeight: 400, color: '#888', marginLeft: 8 }}>
                    · {billableCount} billable · {nonBillableCount} no-charge
                  </span>
                  }
                </span>
                <span style={{ fontSize: 12 }}>
                  <button className="btn btn-sm" onClick={selectAllEntries} style={{ marginRight: 6 }}>Select All</button>
                  <button className="btn btn-sm" onClick={selectNoEntries}>None</button>
                </span>
              </div>
            );
          })()}
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
            {preview.entries.length === 0 ?
          <div style={{ padding: 24, textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                No time entries ready to invoice for this client.
              </div> :

          <div className="table-wrap">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#0F1838' }}>
                      <th style={{ ...th, width: 40 }}></th>
                      <th style={{ ...th, width: 100 }}>Date</th>
                      <th style={th}>Description</th>
                      <th style={{ ...th, width: 110 }}>Biller</th>
                      <th style={{ ...th, width: 60, textAlign: 'right' }}>Hrs</th>
                      <th style={{ ...th, width: 80, textAlign: 'right' }}>Rate</th>
                      <th style={{ ...th, width: 90, textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.entries.map((e, i) => {
                  const hrs = parseFloat(e.units) || 0;
                  const rate = e.rate_used ? parseFloat(e.rate_used) : null;
                  const sel = selectedEntries.has(e.id);
                  const billable = e.billable !== false;
                  const amt = billable && rate ? hrs * rate : null;
                  return (
                    <tr key={e.id} onClick={() => toggleEntry(e.id)}
                    style={{
                      borderBottom: '1px solid #eee',
                      background: sel ? (billable ? '#f0f7fc' : '#fbf7ee') : i % 2 === 0 ? '#fff' : '#fafafa',
                      cursor: 'pointer',
                      fontStyle: billable ? 'normal' : 'italic',
                      color: billable ? '#0F1838' : '#666',
                    }}>
                          <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={sel} readOnly /></td>
                          <td style={td}>{fmtDate(e.entry_date)}</td>
                          <td style={td}>
                            {!billable &&
                              <span style={{ display: 'inline-block', marginRight: 6, padding: '1px 6px', borderRadius: 3, background: '#fbf7ee', border: '1px solid #ead9ad', color: '#b7870a', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontStyle: 'normal' }}>
                                No Charge
                              </span>
                            }
                            {e.description}
                          </td>
                          <td style={{ ...td, color: '#555', fontSize: 12 }}>{e.biller_name || '—'}</td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{hrs.toFixed(2)}</td>
                          <td style={{ ...td, textAlign: 'right', color: '#555' }}>{billable && rate ? fmtMoney(rate) : '—'}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: billable ? 600 : 400 }}>
                            {billable ? (amt != null ? fmtMoney(amt) : '—') : <span style={{ color: '#888', fontStyle: 'italic' }}>No charge</span>}
                          </td>
                        </tr>);

                })}
                  </tbody>
                </table>
              </div>
          }
          </div>

          {/* Expenses */}
          <div className="section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span>Expenses Ready to Invoice ({preview.expenses.length})</span>
            <span style={{ fontSize: 12 }}>
              <button className="btn btn-sm" onClick={selectAllExpenses} style={{ marginRight: 6 }}>Select All</button>
              <button className="btn btn-sm" onClick={selectNoExpenses}>None</button>
            </span>
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
            {preview.expenses.length === 0 ?
          <div style={{ padding: 24, textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                No expenses ready to invoice for this client.
              </div> :

          <div className="table-wrap">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#0F1838' }}>
                      <th style={{ ...th, width: 40 }}></th>
                      <th style={{ ...th, width: 100 }}>Date</th>
                      <th style={th}>Description</th>
                      <th style={{ ...th, width: 130 }}>Category</th>
                      <th style={{ ...th, width: 90, textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.expenses.map((x, i) => {
                  const sel = selectedExpenses.has(x.id);
                  return (
                    <tr key={x.id} onClick={() => toggleExpense(x.id)}
                    style={{ borderBottom: '1px solid #eee', background: sel ? '#f0f7fc' : i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}>
                          <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={sel} readOnly /></td>
                          <td style={td}>{fmtDate(x.expense_date)}</td>
                          <td style={td}>{x.description}</td>
                          <td style={{ ...td, color: '#555', fontSize: 12 }}>{x.category || '—'}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(x.amount)}</td>
                        </tr>);

                })}
                  </tbody>
                </table>
              </div>
          }
          </div>

          {/* ── Wave 3b: Account Statement settings + live preview ──────────
              The attorney configures the date window, whether voided rows
              show, and which trust types appear. Filtered preview tables
              show exactly what will land on the invoice. */}
          <div className="section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span>
              Account Statement
              <span style={{ fontSize: 12, fontWeight: 400, color: '#888', marginLeft: 8 }}>
                · activity within a date window shown on the invoice
              </span>
            </span>
            <label style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={showStatement} onChange={(e) => setShowStatement(e.target.checked)} />
              <span>Include account statement on this invoice</span>
            </label>
          </div>

          {showStatement &&
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="form-row">
              <div className="form-group">
                <label>Statement From</label>
                <input type="date" value={statementFrom}
                  onChange={(e) => setStatementFrom(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Statement To</label>
                <input type="date" value={statementTo}
                  onChange={(e) => setStatementTo(e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                <label style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 0, paddingBottom: 8 }}>
                  <input type="checkbox" checked={includeVoided}
                    onChange={(e) => setIncludeVoided(e.target.checked)} />
                  <span>Include voided items (audit trail)</span>
                </label>
              </div>
            </div>

            <div style={{ marginTop: 4 }}>
              <div style={{ fontSize: 12, color: '#666', fontFamily: 'Arial, sans-serif', marginBottom: 6 }}>
                Trust transaction types to include:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                {[
                  ['deposit', 'Deposits'],
                  ['withdrawal', 'Withdrawals'],
                  ['transfer_to_operating', 'Transfers to Operating'],
                  ['adjustment', 'Adjustments'],
                ].map(([key, label]) => (
                  <label key={key} style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={trustTypes.has(key)}
                      onChange={() => toggleTrustType(key)} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 14, borderTop: '1px solid #eee', paddingTop: 12 }}>
              <button className="btn btn-sm"
                onClick={() => setStatementPreviewOpen(!statementPreviewOpen)}
                style={{ marginRight: 8 }}>
                {statementPreviewOpen ? '▾ Hide' : '▸ Show'} preview ({
                  filteredStatement.payments.length + filteredStatement.refunds.length +
                  filteredStatement.reversals.length + filteredStatement.trust.length
                } {filteredStatement.payments.length + filteredStatement.refunds.length +
                  filteredStatement.reversals.length + filteredStatement.trust.length === 1 ? 'row' : 'rows'})
              </button>
              <span style={{ fontSize: 11, color: '#888', fontFamily: 'Arial, sans-serif' }}>
                {filteredStatement.payments.length} payment{filteredStatement.payments.length === 1 ? '' : 's'}
                {' · '}{filteredStatement.refunds.length} refund{filteredStatement.refunds.length === 1 ? '' : 's'}
                {' · '}{filteredStatement.reversals.length} reversal{filteredStatement.reversals.length === 1 ? '' : 's'}
                {' · '}{filteredStatement.trust.length} trust txn{filteredStatement.trust.length === 1 ? '' : 's'}
              </span>
            </div>

            {statementPreviewOpen && (() => {
              const tableSt = { width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'Arial, sans-serif' };
              const headSt  = { background: '#eee', color: '#0F1838' };
              const thS     = { padding: '5px 8px', textAlign: 'left', fontWeight: 600 };
              const tdS     = { padding: '5px 8px' };
              const sectionTitle = { color: '#888', textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.04em', marginTop: 14, marginBottom: 4, fontFamily: 'Arial, sans-serif' };
              const renderVoidedBadge = (row) => row.status === 'voided' ?
                <span style={{ display: 'inline-block', marginRight: 6, padding: '1px 6px', borderRadius: 3, background: '#f3f3f3', color: '#666', border: '1px solid #d6d6d6', fontSize: 9, fontWeight: 600 }}>VOIDED</span> :
                null;
              return (
              <div style={{ marginTop: 12 }}>
                {filteredStatement.payments.length === 0 && filteredStatement.refunds.length === 0 &&
                 filteredStatement.reversals.length === 0 && filteredStatement.trust.length === 0 &&
                  <div style={{ padding: 16, textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                    No account activity in the selected window with the current filters.
                  </div>
                }
                {filteredStatement.payments.length > 0 &&
                <>
                  <div style={sectionTitle}>Payments ({filteredStatement.payments.length})</div>
                  <table style={tableSt}>
                    <thead><tr style={headSt}>
                      <th style={{ ...thS, width: 100 }}>Date</th>
                      <th style={{ ...thS, width: 140 }}>Method</th>
                      <th style={{ ...thS, width: 140 }}>Ref #</th>
                      <th style={thS}>Payor</th>
                      <th style={{ ...thS, width: 90, textAlign: 'right' }}>Amount</th>
                    </tr></thead>
                    <tbody>
                      {filteredStatement.payments.map((p) => {
                        const v = p.status === 'voided';
                        return (
                          <tr key={p.id} style={{ borderBottom: '1px solid #eee', opacity: v ? 0.6 : 1, fontStyle: v ? 'italic' : 'normal' }}>
                            <td style={tdS}>{fmtDate(p.payment_date)}</td>
                            <td style={tdS}>{renderVoidedBadge(p)}{methodLabel(p.method)}</td>
                            <td style={{ ...tdS, fontFamily: 'monospace', color: '#666' }}>{p.reference_number || '—'}</td>
                            <td style={tdS}>{p.payor || '—'}</td>
                            <td style={{ ...tdS, textAlign: 'right', fontFamily: 'monospace', fontWeight: v ? 400 : 600, textDecoration: v ? 'line-through' : 'none' }}>
                              {fmtMoney(p.amount)}
                            </td>
                          </tr>);
                      })}
                    </tbody>
                  </table>
                </>
                }
                {filteredStatement.refunds.length > 0 &&
                <>
                  <div style={sectionTitle}>Refunds ({filteredStatement.refunds.length})</div>
                  <table style={tableSt}>
                    <thead><tr style={headSt}>
                      <th style={{ ...thS, width: 100 }}>Date</th>
                      <th style={{ ...thS, width: 140 }}>Method</th>
                      <th style={{ ...thS, width: 140 }}>Ref #</th>
                      <th style={thS}>Reason</th>
                      <th style={{ ...thS, width: 90, textAlign: 'right' }}>Amount</th>
                    </tr></thead>
                    <tbody>
                      {filteredStatement.refunds.map((r) => {
                        const v = r.status === 'voided';
                        return (
                          <tr key={r.id} style={{ borderBottom: '1px solid #eee', opacity: v ? 0.6 : 1, fontStyle: v ? 'italic' : 'normal' }}>
                            <td style={tdS}>{fmtDate(r.refund_date)}</td>
                            <td style={tdS}>{renderVoidedBadge(r)}{methodLabel(r.method)}</td>
                            <td style={{ ...tdS, fontFamily: 'monospace', color: '#666' }}>{r.reference_number || '—'}</td>
                            <td style={tdS}>{r.reason || '—'}</td>
                            <td style={{ ...tdS, textAlign: 'right', fontFamily: 'monospace', fontWeight: v ? 400 : 600, textDecoration: v ? 'line-through' : 'none' }}>
                              {fmtMoney(r.amount)}
                            </td>
                          </tr>);
                      })}
                    </tbody>
                  </table>
                </>
                }
                {filteredStatement.reversals.length > 0 &&
                <>
                  <div style={sectionTitle}>Reversals ({filteredStatement.reversals.length})</div>
                  <table style={tableSt}>
                    <thead><tr style={headSt}>
                      <th style={{ ...thS, width: 100 }}>Date</th>
                      <th style={{ ...thS, width: 180 }}>Original Payment</th>
                      <th style={thS}>Reason</th>
                    </tr></thead>
                    <tbody>
                      {filteredStatement.reversals.map((r) => {
                        const v = r.status === 'voided';
                        return (
                          <tr key={r.id} style={{ borderBottom: '1px solid #eee', opacity: v ? 0.6 : 1, fontStyle: v ? 'italic' : 'normal' }}>
                            <td style={tdS}>{fmtDate(r.reversal_date)}</td>
                            <td style={{ ...tdS, color: '#666' }}>
                              {r.source_payment_date ? fmtDate(r.source_payment_date) : '—'}
                              {r.source_payment_amount != null && <> — <span style={{ fontFamily: 'monospace' }}>{fmtMoney(r.source_payment_amount)}</span></>}
                            </td>
                            <td style={tdS}>{renderVoidedBadge(r)}{r.reason || '—'}</td>
                          </tr>);
                      })}
                    </tbody>
                  </table>
                </>
                }
                {filteredStatement.trust.length > 0 &&
                <>
                  <div style={sectionTitle}>Trust Transactions ({filteredStatement.trust.length})</div>
                  <table style={tableSt}>
                    <thead><tr style={headSt}>
                      <th style={{ ...thS, width: 100 }}>Date</th>
                      <th style={{ ...thS, width: 160 }}>Type</th>
                      <th style={{ ...thS, width: 110 }}>Method</th>
                      <th style={thS}>Payer / Payee</th>
                      <th style={{ ...thS, width: 120 }}>Ref #</th>
                      <th style={{ ...thS, width: 110, textAlign: 'right' }}>Amount</th>
                    </tr></thead>
                    <tbody>
                      {filteredStatement.trust.map((t) => {
                        const amt = parseFloat(t.amount) || 0;
                        return (
                          <tr key={t.id} style={{ borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                            <td style={tdS}>{fmtDate(t.txn_date)}</td>
                            <td style={tdS}>{trustTypeLabel(t.type)}</td>
                            <td style={tdS}>{methodLabel(t.payment_method) || '—'}</td>
                            <td style={tdS}>
                              {t.payer_payee || '—'}
                              {t.description && <span style={{ color: '#888', display: 'block', fontSize: 11, marginTop: 2 }}>{t.description}</span>}
                            </td>
                            <td style={{ ...tdS, fontFamily: 'monospace', color: '#666', fontSize: 11 }}>{t.reference_number || '—'}</td>
                            <td style={{ ...tdS, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: amt < 0 ? '#B85042' : '#1a7a5e' }}>
                              {amt < 0 ? '(' + fmtMoney(Math.abs(amt)) + ')' : fmtMoney(amt)}
                            </td>
                          </tr>);
                      })}
                    </tbody>
                  </table>
                </>
                }
              </div>);
            })()}

            {preview.account_summary &&
            <div style={{ marginTop: 14, borderTop: '1px solid #eee', paddingTop: 12, fontFamily: 'Arial, sans-serif', fontSize: 12 }}>
              <div style={{ color: '#888', textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.04em', marginBottom: 6 }}>
                Overall Account Summary (shown at bottom of invoice)
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                <span style={{ color: '#666' }}>Trust Account Balance</span>
                <span style={{ fontFamily: 'monospace' }}>{fmtMoney(preview.account_summary.trust_balance)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                <span style={{ color: '#666' }}>Total Owed (All Invoices)</span>
                <span style={{ fontFamily: 'monospace' }}>{
                  preview.account_summary.total_outstanding > 0 ?
                  '(' + fmtMoney(preview.account_summary.total_outstanding) + ')' :
                  fmtMoney(0)
                }</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 0', borderTop: '1px solid #ccc', marginTop: 4, fontWeight: 700 }}>
                <span>Net Account Balance</span>
                <span style={{ fontFamily: 'monospace' }}>{(() => {
                  const net = (parseFloat(preview.account_summary.trust_balance) || 0) -
                              (parseFloat(preview.account_summary.total_outstanding) || 0);
                  return net < 0 ? '(' + fmtMoney(Math.abs(net)) + ')' : fmtMoney(net);
                })()}</span>
              </div>
            </div>
            }
          </div>
          }

          {/* Invoice details + totals — side by side on wide screens, stacked on mobile */}
          <div className="gen-invoice-grid">
            <div className="card" style={{ margin: 0 }}>
              <div className="card-title" style={{ fontSize: 14 }}>Invoice Details</div>
              <div className="form-row">
                <div className="form-group">
                  <label>Issue Date</label>
                  <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label>Subject <span style={{ fontWeight: 400, color: '#B85042' }}>(required)</span></label>
                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} required
              placeholder="e.g. Professional services through May 31, 2026" />
              </div>
              <div className="form-group">
                <label>Notes <span style={{ fontWeight: 400, color: '#888' }}>(visible on the invoice)</span></label>
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes for the client" />
              </div>
              <div className="form-group">
                <label>Internal Notes <span style={{ fontWeight: 400, color: '#888' }}>(staff only — never on the invoice)</span></label>
                <textarea rows={2} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Optional notes for your records" />
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: '0 0 140px' }}>
                  <label>Discount ($)</label>
                  <input type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0.00" />
                </div>
                <div className="form-group" style={{ flex: '0 0 140px' }}>
                  <label>Tax ($)</label>
                  <input type="number" min="0" step="0.01" value={tax} onChange={(e) => setTax(e.target.value)} placeholder="0.00" />
                </div>
              </div>
            </div>

            <div className="card gen-invoice-summary" style={{ margin: 0 }}>
              <div className="card-title" style={{ fontSize: 14 }}>Summary</div>
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ color: '#666' }}>Time entries</span>
                  <span>{selectedEntries.size}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ color: '#666' }}>Expenses</span>
                  <span>{selectedExpenses.size}</span>
                </div>
                <div style={{ borderTop: '1px solid #eee', marginTop: 8, paddingTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#666' }}>Professional Services</span>
                    <span style={{ fontFamily: 'monospace' }}>{fmtMoney(totals.fees)}</span>
                  </div>
                  {totals.noChargeCount > 0 &&
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#888', fontStyle: 'italic', fontSize: 12 }}>
                    <span>No-charge time ({totals.noChargeCount} {totals.noChargeCount === 1 ? 'entry' : 'entries'})</span>
                    <span style={{ fontFamily: 'monospace' }}>{totals.noChargeHours.toFixed(2)} hr · $0.00</span>
                  </div>
                  }
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#666' }}>Costs & Expenses</span>
                    <span style={{ fontFamily: 'monospace' }}>{fmtMoney(totals.exp)}</span>
                  </div>
                  {totals.discount > 0 &&
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span style={{ color: '#666' }}>Discount</span>
                      <span style={{ fontFamily: 'monospace' }}>({fmtMoney(totals.discount)})</span>
                    </div>
                }
                  {totals.tax > 0 &&
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span style={{ color: '#666' }}>Tax</span>
                      <span style={{ fontFamily: 'monospace' }}>{fmtMoney(totals.tax)}</span>
                    </div>
                }
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '2px solid #0F1838', marginTop: 4 }}>
                    <span style={{ fontWeight: 700 }}>Total</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16 }}>{fmtMoney(totals.total)}</span>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <button className="btn btn-primary" onClick={handleCreate} disabled={loading || !selectedEntries.size && !selectedExpenses.size} style={{ width: '100%' }}>
                  {loading ? 'Creating...' : 'Create Draft Invoice'}
                </button>
                <p style={{ fontSize: 11, color: '#888', fontFamily: 'Arial, sans-serif', textAlign: 'center', marginTop: 8 }}>
                  You can review and edit the draft before issuing it.
                </p>
              </div>
            </div>
          </div>
        </>
      }
    </div>);

}